import { nextTick, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { CareType } from '@/types/care_type';

export function useFavoritesFocus(
  isLoading: Readonly<Ref<boolean>>,
  careType: Readonly<Ref<CareType | null>>,
  currentPage: Readonly<Ref<number>>,
  showRemoveConfirmation: Readonly<Ref<boolean>>
) {
  const pageHeading = ref<HTMLElement | null>(null);
  const pageSection = ref<HTMLElement | null>(null);
  let removalFocusIndex: number | null = null;
  let removalTrigger: HTMLElement | null = null;
  let focusResultsWhenLoaded = false;

  watch([isLoading, careType, currentPage], async ([loading, type, page], [, previousType, previousPage]) => {
    if (type !== previousType) removalFocusIndex = null;
    if ((type !== previousType || page !== previousPage) && removalFocusIndex === null) {
      focusResultsWhenLoaded = true;
    }
    if (loading || showRemoveConfirmation.value) return;
    await nextTick();
    if (isLoading.value || showRemoveConfirmation.value) return;
    if (removalFocusIndex !== null) focusAdjacentCard();
    else if (focusResultsWhenLoaded) {
      focusResultsWhenLoaded = false;
      pageHeading.value?.focus();
    }
  });

  function rememberRemovalTrigger(event: MouseEvent): void {
    removalTrigger = event.currentTarget as HTMLElement;
  }

  function restoreRemovalTrigger(): void {
    nextTick(() => {
      if (removalTrigger?.isConnected) removalTrigger.focus({ preventScroll: true });
    });
  }

  function removalIndex(): number {
    const cards = Array.from(pageSection.value?.querySelectorAll<HTMLElement>('[data-favorite-card]') ?? []);
    return Math.max(
      0,
      cards.findIndex((card) => removalTrigger !== null && card.contains(removalTrigger))
    );
  }

  async function focusAfterRemoval(index: number): Promise<void> {
    removalFocusIndex = index;
    focusResultsWhenLoaded = false;
    await nextTick();
    focusAdjacentCard();
  }

  function focusAdjacentCard(): void {
    const cards = pageSection.value?.querySelectorAll<HTMLElement>('[data-favorite-card]');
    const target = cards?.[Math.min(removalFocusIndex ?? 0, cards.length - 1)] ?? pageHeading.value;
    target?.focus({ preventScroll: true });
    if (cards?.length) removalFocusIndex = null;
  }

  return { pageHeading, pageSection, rememberRemovalTrigger, restoreRemovalTrigger, removalIndex, focusAfterRemoval };
}
